import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, BarChart3, TrendingUp, MapPin, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { value: 7, label: '7 jours' },
  { value: 30, label: '30 jours' },
  { value: 90, label: '90 jours' },
  { value: 365, label: '1 an' },
];

export default function VisitStats() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/visit-stats?days=${days}`, { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      console.error('Failed to load visit stats:', e);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const maxCountryCount = stats?.by_country?.[0]?.count || 1;
  const maxPathCount = stats?.by_path?.[0]?.count || 1;
  const maxDayCount = Math.max(...(stats?.by_day?.map((d) => d.count) || [1]));

  return (
    <div className="card mt-8" data-testid="visit-stats-section">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h3 className="text-xl font-bold text-[#2E332D] inline-flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          <BarChart3 size={22} className="text-[#4A6B48]" /> Statistiques de visites du site
        </h3>
        <div className="flex gap-1">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              className={`px-3 py-1 text-xs font-bold rounded border-2 transition-all ${
                days === r.value ? 'bg-[#4A6B48] border-[#4A6B48] text-white' : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48]'
              }`}
              data-testid={`stats-range-${r.value}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : stats ? (
        <>
          {/* Compteurs principaux */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#4A6B48]/5 border border-[#4A6B48]/20 rounded-lg p-4" data-testid="stat-card-total">
              <div className="flex items-center gap-2 text-[#4A6B48] mb-1">
                <Eye size={16} /> <span className="text-xs font-bold uppercase tracking-wide">Visites ({days}j)</span>
              </div>
              <div className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {stats.total_visits}
              </div>
            </div>
            <div className="bg-[#D87D4A]/5 border border-[#D87D4A]/20 rounded-lg p-4" data-testid="stat-card-unique">
              <div className="flex items-center gap-2 text-[#D87D4A] mb-1">
                <Globe size={16} /> <span className="text-xs font-bold uppercase tracking-wide">Visiteurs uniques</span>
              </div>
              <div className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {stats.unique_visitors}
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="stat-card-all-time">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <TrendingUp size={16} /> <span className="text-xs font-bold uppercase tracking-wide">Total cumulé</span>
              </div>
              <div className="text-3xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {stats.total_all_time}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pays */}
            <div data-testid="stats-by-country">
              <h4 className="text-sm font-bold text-[#2E332D] mb-3 inline-flex items-center gap-1">
                <MapPin size={14} className="text-[#4A6B48]" /> Visites par pays
              </h4>
              {stats.by_country.length === 0 ? (
                <p className="text-xs text-[#5C665A]">Aucune donnée sur la période</p>
              ) : (
                <div className="space-y-2">
                  {stats.by_country.slice(0, 10).map((c) => (
                    <div key={c.country} className="flex items-center gap-2 text-sm">
                      <div className="w-28 truncate text-[#2E332D]">{c.country}</div>
                      <div className="flex-1 bg-[#F9F8F6] rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-[#4A6B48]"
                          style={{ width: `${Math.max(2, (c.count / maxCountryCount) * 100)}%` }}
                        ></div>
                      </div>
                      <div className="w-10 text-right text-xs font-bold text-[#5C665A]">{c.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pages */}
            <div data-testid="stats-by-page">
              <h4 className="text-sm font-bold text-[#2E332D] mb-3">Pages les plus visitées</h4>
              {stats.by_path.length === 0 ? (
                <p className="text-xs text-[#5C665A]">Aucune donnée sur la période</p>
              ) : (
                <div className="space-y-2">
                  {stats.by_path.map((p) => (
                    <div key={p.path} className="flex items-center gap-2 text-sm">
                      <div className="w-32 truncate text-[#2E332D] font-mono text-xs">{p.path || '/'}</div>
                      <div className="flex-1 bg-[#F9F8F6] rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-[#D87D4A]"
                          style={{ width: `${Math.max(2, (p.count / maxPathCount) * 100)}%` }}
                        ></div>
                      </div>
                      <div className="w-10 text-right text-xs font-bold text-[#5C665A]">{p.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fréquence par jour (bar chart compact) */}
          {stats.by_day.length > 0 && (
            <div className="mt-6" data-testid="stats-by-day">
              <h4 className="text-sm font-bold text-[#2E332D] mb-3">Fréquence quotidienne</h4>
              <div className="flex items-end gap-1 h-24 bg-[#F9F8F6] rounded p-2">
                {stats.by_day.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center group relative" title={`${d.date} : ${d.count} visites`}>
                    <div
                      className="w-full bg-[#4A6B48] rounded-t hover:bg-[#3B5539]"
                      style={{ height: `${Math.max(3, (d.count / maxDayCount) * 100)}%`, minHeight: '3px' }}
                    ></div>
                    <span className="absolute -top-6 bg-[#2E332D] text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                      {d.count}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs text-[#5C665A] mt-1">
                <span>{stats.by_day[0]?.date}</span>
                <span>{stats.by_day[stats.by_day.length - 1]?.date}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-center text-[#5C665A] py-8">Aucune donnée disponible.</p>
      )}
    </div>
  );
}
